import type { Deal, DealStage } from "../../lib/types";
import { agreementSigned, homeownerColorsAuthorized, insuranceScopeOnFile, scopeEntered } from "../../lib/buildSubmitGates";

/**
 * Stage requirements — hard gates that block advancement unless met.
 * Each requirement has a `check` function that returns true when satisfied.
 * `label` is shown in the checklist. `required` = hard block (vs. warning).
 */

export interface StageRequirement {
  key: string;
  label: string;
  required: boolean; // true = hard block, false = warning
  check: (d: Deal) => boolean;
}

type RequirementsMap = Partial<Record<DealStage, StageRequirement[]>>;

export const STAGE_REQUIREMENTS: RequirementsMap = {
  // Drone phase
  appointment_received: [
    // 'bypassed' = the executive path (Oct 22, 2026): the owner's role skips ASM review.
    { key: "asm_approved", label: "ASM has reviewed appointment (or executive path)", required: true, check: (d) => d.asm_review_status === "approved" || d.asm_review_status === "bypassed" },
  ],
  drone_scheduled: [
    { key: "pilot_assigned", label: "Drone pilot assigned", required: true, check: (d) => !!d.assigned_drone_pilot_id },
    { key: "date_set", label: "Drone date scheduled", required: true, check: (d) => !!d.drone_scheduled_date },
  ],
  drone_complete: [
    { key: "drone_done", label: "Drone flight completed", required: true, check: (d) => !!d.drone_completed_date },
  ],
  report_approved: [
    { key: "qc_passed", label: "QC review passed", required: true, check: (d) => d.report_qc_status === "approved" },
  ],

  // Drone → Leads transition
  initial_contact: [
    { key: "asm_gate2", label: "ASM approved transition to leads", required: true, check: (d) => d.asm_review_status === "approved" },
  ],

  // Leads phase
  presentation_scheduled: [
    { key: "pres_date", label: "Presentation date & time set", required: true, check: (d) => !!d.presentation_date },
    { key: "pres_type", label: "Live or Call chosen", required: true, check: (d) => d.presentation_type === "live" || d.presentation_type === "call" },
  ],
  follow_up: [
    // "Presentation Completed" button — no hard data requirements, just the button click
  ],

  // Claims phase — "Claim Called In" gate (transition from any leads stage)
  assign_roof_specialist: [
    { key: "has_insurance", label: "Insurance company entered", required: true, check: (d) => !!d.insurance_company },
    { key: "has_claim", label: "Claim number entered", required: true, check: (d) => !!d.claim_number },
    { key: "has_dol", label: "Date of loss/discovery entered", required: true, check: (d) => !!d.date_of_loss },
  ],

  // Claims: Assign RS → PRE Ready
  pre_inspection_ready: [
    { key: "rs_assigned", label: "Roof specialist assigned", required: true, check: (d) => !!d.assigned_rs_id },
  ],

  // Claims: PRE Ready → Adjuster Meeting Ready
  adjuster_meeting_ready: [
    { key: "pre_done", label: "Pre-inspection completed", required: true, check: (d) => !!d.pre_inspection_completed },
    { key: "adj_date", label: "Adjuster meeting date & time set", required: true, check: (d) => !!d.adjuster_meeting_date },
  ],

  // Claims: Adjuster Meeting Ready → Waiting on Claims
  waiting_on_claims: [
    { key: "adj_outcome", label: "Adjuster meeting notes recorded", required: true, check: (d) => !!d.adjuster_meeting_outcome },
  ],

  // Claims → Builds: Waiting on Claims → Install Meeting
  install_meeting: [
    { key: "insurance_uploaded", label: "Insurance paperwork uploaded", required: true, check: (d) => !!d.insurance_paperwork_uploaded },
  ],

  // Reinspections phase
  reinspection_ready: [
    { key: "contingency", label: "Signed contingency from homeowner", required: true, check: (d) => !!d.contingency_signed },
    { key: "insurance_docs", label: "Insurance paperwork uploaded", required: true, check: (d) => !!d.insurance_paperwork_uploaded },
    { key: "ho_verified", label: "Homeowner info verified (phone, email, name)", required: true, check: (d) => !!d.ho_info_verified },
    { key: "hover", label: "Hover measurements ordered", required: true, check: (d) => !!d.hover_ordered },
    { key: "apex", label: "Apex full-roof estimate generated", required: true, check: (d) => !!d.apex_estimate_generated },
  ],
  reinspection_filed: [
    { key: "email1_sent", label: "Reinspection Email 1 sent", required: true, check: (d) => !!d.reinspection_email1_sent_at },
  ],

  // Builds phase
  // Oct 22, 2026 (Greg): the hard gates to submit a build are the signed
  // agreement, the homeowner's shingle + drip edge color authorization, the
  // insurance scope on file, and the first payment (or a plan for it). The
  // first three are checked here and, for real, in the database by
  // deals_gate_build_documents(); the money is asked at the Submit click and
  // enforced by deals_gate_build_payment_plan(). The rules live in
  // src/lib/buildSubmitGates.ts — keep them in step with the SQL.
  build_submitted: [
    { key: "agreement_signed", label: "Install agreement signed by the homeowner", required: true, check: agreementSigned },
    { key: "colors_authorized", label: "Shingle & drip edge colors authorized by the homeowner", required: true, check: homeownerColorsAuthorized },
    { key: "insurance_uploaded", label: "Insurance scope uploaded", required: true, check: insuranceScopeOnFile },
    { key: "scope_entered", label: "Scope entered", required: true, check: scopeEntered },
  ],
  schedule_build: [
    // One route (Oct 22, 2026): a submitted build is approved ONLY on the Build
    // Approvals board (Dispatch), by Chad or Robert — build_approve() in the
    // database. Nothing on the deal page or a pipeline card can advance it, so
    // this requirement is never met here; it points people at the board.
    { key: "board_approval", label: "Approved on the Build Approvals board (Chad or Robert)", required: true, check: () => false },
  ],
  build_scheduled: [
    { key: "build_date", label: "Build date scheduled", required: true, check: (d) => !!d.build_scheduled_date },
  ],
  roof_complete: [
    { key: "build_done", label: "Build completed date set", required: true, check: (d) => !!d.build_completed_date },
  ],

  // Closing phase
  commission_ready: [
    { key: "build_complete", label: "Build completed", required: true, check: (d) => !!d.build_completed_date },
    { key: "payment", label: "Pre-build payment received", required: true, check: (d) => !!d.pre_build_amount_received && Number(d.pre_build_amount_received) > 0 },
  ],
  commission_approved: [
    { key: "comm_ready", label: "Commission reviewed", required: true, check: (d) => d.stage === "commission_ready" || d.stage === "commission_approved" || d.stage === "commission_paid" },
  ],
  commission_paid: [
    { key: "comm_approved", label: "Commission approved by Chad", required: true, check: (d) => d.stage === "commission_approved" || d.stage === "commission_paid" },
  ],
  closed_won: [
    // Warranty registration date dropped as a close gate per Greg (May 21, 2026)
    // — not needed. The date is still captured via the warranty-registration
    // workflow (Jennifer's QC board / dispatch WarrantyTab) where applicable.
    { key: "all_paid", label: "Final payment collected", required: true, check: (d) => !!d.final_payment_paid && Number(d.final_payment_paid) > 0 },
  ],
};

/** Get requirements for advancing TO the given stage. */
export function getRequirements(targetStage: DealStage): StageRequirement[] {
  return STAGE_REQUIREMENTS[targetStage] ?? [];
}

/** Check if all hard-gate requirements are met. */
export function canAdvanceTo(d: Deal, targetStage: DealStage): { allowed: boolean; results: Array<StageRequirement & { met: boolean }> } {
  const reqs = getRequirements(targetStage);
  const results = reqs.map((r) => ({ ...r, met: r.check(d) }));
  const allowed = results.filter((r) => r.required).every((r) => r.met);
  return { allowed, results };
}

/** Ordered stage progression — what's the next logical stage? */
const STAGE_ORDER: DealStage[] = [
  "pending_asm_review",
  "appointment_received",
  "drone_scheduled",
  "drone_complete",
  "report_in_progress",
  "qc_review",
  "report_approved",
  "initial_contact",
  "presentation_scheduled",
  "follow_up",
  "waiting",
  "assign_roof_specialist",
  "pre_inspection_ready",
  "adjuster_meeting_ready",
  "waiting_on_claims",
  "partial_approval",
  "docs_and_contingency",
  "reinspection_ready",
  "reinspection_filed",
  "install_meeting",
  "build_submitted",
  "schedule_build",
  "build_scheduled",
  "roof_complete",
  "trades_in_progress",
  "commission_ready",
  "commission_approved",
  "commission_paid",
  "closed_won",
];

export function getNextStage(current: DealStage | null): DealStage | null {
  if (!current) return null;
  const idx = STAGE_ORDER.indexOf(current);
  if (idx === -1 || idx >= STAGE_ORDER.length - 1) return null;
  return STAGE_ORDER[idx + 1];
}

export function getStagePhaseName(stage: DealStage | null): string {
  if (!stage) return "Unknown";
  const stageStr = stage.replace(/_/g, " ");
  return stageStr.charAt(0).toUpperCase() + stageStr.slice(1);
}
